import React, { PropsWithChildren, useContext } from "react";
import { ExtraCategory } from "../../domain/ExtraCategory.enum";
import { CategoryLimits } from "../../domain/entities/CategoryLimits";
import { ValidationIssue } from "../../domain/entities/ValidationIssue";

/**
 * What the *Limits per category* field needs besides its form data: the section loaded by
 * `GetAccommodationCategoryLimits`, the accommodation name typed so far and the issues of the
 * last rejected save.
 */
export interface CategoryLimitsContextValue {
  /** `undefined` while loading, with the flag off or when the call failed. */
  readonly limits?: CategoryLimits;
  /** Name typed in the form, in the user locale, for the help texts. Empty while not typed. */
  readonly accommodationName: string;
  /** Issues of the last rejected save that target a block. */
  readonly serverIssues: readonly ValidationIssue[];
  /** Drops the server issues of one block, when the user edits it. */
  readonly clearServerIssues: (category: ExtraCategory) => void;
}

export const emptyCategoryLimitsContext: CategoryLimitsContextValue = Object.freeze({
  limits: undefined,
  accommodationName: "",
  serverIssues: [],
  clearServerIssues: () => undefined,
});

const CategoryLimitsContext = React.createContext<CategoryLimitsContextValue>(
  emptyCategoryLimitsContext
);

export function useCategoryLimitsContext(): CategoryLimitsContextValue {
  return useContext(CategoryLimitsContext);
}

export function CategoryLimitsProvider({
  value,
  children,
}: PropsWithChildren<{ value: CategoryLimitsContextValue }>) {
  return <CategoryLimitsContext.Provider value={value}>{children}</CategoryLimitsContext.Provider>;
}
